/**
 * AWALANE — Interactions de la fiche produit
 *
 * - Tiroirs animés : accordéons, sélecteur de taille desktop,
 *   sélecteurs couleur / taille de la barre mobile.
 * - Galerie plein écran.
 * - Indicateur de défilement de la galerie.
 * - Bouton panier de la barre mobile.
 *
 * Aucune dépendance. Chargé par sections/main-product.liquid.
 */
(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────
     Mouvement
     Une seule durée et une seule courbe pour l'ouverture comme
     pour la fermeture. Quand un tiroir s'ouvre pendant qu'un
     autre se ferme, les deux avancent au même rythme : la hauteur
     totale varie dans un seul sens, sans aller-retour.
  ──────────────────────────────────────────────────────── */
  const MOTION = {
    duration: 300,
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ────────────────────────────────────────────────────────
     Collapsible
     Un <details> dont le panneau s'anime en hauteur.

     Contrat CSS du panneau :
     - fermé : height 0, overflow hidden, opacity 0 ;
     - [open] : height auto, opacity 1 ;
     - aucun padding, aucune bordure, aucune transition CSS.
       La hauteur animée est ainsi exactement la hauteur visible.

     Déroulé :
     - l'état logique (expanded) et aria-expanded changent au clic ;
     - pendant une fermeture, le <details> porte .is-closing et
       garde l'attribut open jusqu'à la fin réelle de l'animation ;
     - à la fin, les styles inline sont retirés et le CSS reprend la main ;
     - un clic pendant une animation repart de la hauteur courante.
  ──────────────────────────────────────────────────────── */
  class Collapsible {
    constructor(details, panel, onOpen) {
      this.details  = details;
      this.panel    = panel;
      this.summary  = details.querySelector(':scope > summary');
      this.onOpen   = onOpen || null;
      this.expanded = details.hasAttribute('open');
      this._cancel  = null;

      this.summary.setAttribute('aria-expanded', String(this.expanded));
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });
    }

    get id() { return this.details.id; }

    toggle() {
      if (this.expanded) this.close();
      else this.open();
    }

    open() {
      if (this.expanded) return;
      this.expanded = true;
      this._animate(true);
      if (this.onOpen) this.onOpen(this);
    }

    close() {
      if (!this.expanded) return;
      this.expanded = false;
      this._animate(false);
    }

    _animate(opening) {
      const { details, panel, summary } = this;

      if (this._cancel) this._cancel();
      summary.setAttribute('aria-expanded', String(opening));
      details.classList.toggle('is-closing', !opening);

      /* Point de départ mesuré avant tout changement d'attribut :
         0 si le tiroir était fermé, hauteur courante s'il était
         ouvert ou en cours d'animation. */
      const fromHeight  = panel.getBoundingClientRect().height;
      const fromOpacity = getComputedStyle(panel).opacity;

      if (opening) details.setAttribute('open', '');

      panel.style.transition = 'none';
      panel.style.height     = `${fromHeight}px`;
      panel.style.opacity    = fromOpacity;

      const toHeight = opening ? panel.scrollHeight : 0;

      if (reducedMotion.matches || fromHeight === toHeight) {
        this._settle(opening);
        return;
      }

      void panel.offsetHeight; /* valide le point de départ avant la transition */

      panel.style.transition =
        `height ${MOTION.duration}ms ${MOTION.easing}, opacity ${MOTION.duration}ms ${MOTION.easing}`;
      panel.style.height  = `${toHeight}px`;
      panel.style.opacity = opening ? '1' : '0';

      const finish = () => {
        cleanup();
        this._settle(opening);
      };
      const onEnd = (event) => {
        if (event.target === panel && event.propertyName === 'height') finish();
      };
      /* Filet de sécurité si transitionend ne se déclenche pas
         (onglet en arrière-plan, contenu modifié en cours de route). */
      const fallback = setTimeout(finish, MOTION.duration + 100);
      const cleanup = () => {
        panel.removeEventListener('transitionend', onEnd);
        clearTimeout(fallback);
        this._cancel = null;
      };

      panel.addEventListener('transitionend', onEnd);
      this._cancel = cleanup;
    }

    _settle(opening) {
      if (!opening) {
        this.details.removeAttribute('open');
        this.details.classList.remove('is-closing');
      }
      this.panel.style.removeProperty('transition');
      this.panel.style.removeProperty('height');
      this.panel.style.removeProperty('opacity');
    }
  }

  /* ────────────────────────────────────────────────────────
     CollapsibleGroup
     Un seul tiroir ouvert à la fois dans le groupe.
     dismissible : fermeture au clic extérieur et à la touche Échap.
  ──────────────────────────────────────────────────────── */
  class CollapsibleGroup {
    constructor(detailsList, panelSelector, { dismissible = false } = {}) {
      this.items = Array.from(detailsList)
        .map((details) => {
          const panel = details.querySelector(panelSelector);
          if (!panel) return null;
          return new Collapsible(details, panel, (opened) => this.closeAll(opened));
        })
        .filter(Boolean);

      if (dismissible && this.items.length) {
        document.addEventListener('click', (event) => {
          if (!this.items.some((item) => item.details.contains(event.target))) this.closeAll();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeAll();
        });
      }
    }

    closeAll(except) {
      this.items.forEach((item) => { if (item !== except) item.close(); });
    }

    find(id) {
      return this.items.find((item) => item.id === id) || null;
    }
  }

  /* ────────────────────────────────────────────────────────
     Sélection de taille
     Les boutons visibles pilotent les radios natifs Shopify
     (masqués) rattachés au formulaire produit.
  ──────────────────────────────────────────────────────── */
  function syncSizeRadio(optionName, value) {
    const radio = Array.from(document.querySelectorAll('input[type="radio"]'))
      .find((input) => input.name === `options[${optionName}]` && input.value === value);
    if (!radio) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function bindSizeButtons({ buttons, valueAttr, labelEl, drawer }) {
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset[valueAttr];
        buttons.forEach((b) => b.classList.toggle('is-active', b === button));
        if (labelEl) labelEl.textContent = value;
        syncSizeRadio(button.dataset.optionName, value);
        if (drawer) drawer.close();
      });
    });
  }

  /* ────────────────────────────────────────────────────────
     Galerie plein écran
  ──────────────────────────────────────────────────────── */
  class GalleryOverlay {
    constructor() {
      this.overlay  = document.getElementById('galleryOverlay');
      this.content  = document.getElementById('galleryContent');
      this.closeBtn = document.getElementById('galleryClose');
      this.isOpen   = false;
      this._onKey   = (event) => { if (event.key === 'Escape') this.close(); };
      if (!this.overlay || !this.content) return;

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) this.close();
      });
    }

    /* Version haute définition via le paramètre width du CDN Shopify ;
       retour à l'image d'origine si elle ne charge pas. */
    static largeSrc(src) {
      try {
        const url = new URL(src, window.location.href);
        url.searchParams.set('width', '2048');
        return url.toString();
      } catch (_) {
        return src;
      }
    }

    open(images) {
      if (!this.overlay || !this.content || this.isOpen) return;
      this.isOpen = true;
      this.content.innerHTML = '';

      images.forEach((source) => {
        const img = new Image();
        img.alt      = source.alt || '';
        img.loading  = 'lazy';
        img.decoding = 'async';
        img.onerror  = () => { img.onerror = null; img.src = source.currentSrc || source.src; };
        img.src      = GalleryOverlay.largeSrc(source.currentSrc || source.src);
        this.content.appendChild(img);
      });

      this.overlay.style.opacity = '0';
      this.overlay.style.display = 'flex';
      void this.overlay.offsetHeight;
      this.overlay.style.transition = `opacity 180ms ${MOTION.easing}`;
      this.overlay.style.opacity    = '1';

      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKey);
      if (this.closeBtn) this.closeBtn.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.overlay.style.opacity = '0';
      setTimeout(() => {
        if (this.isOpen) return;
        this.overlay.style.display = 'none';
        this.overlay.style.removeProperty('transition');
      }, 180);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
    }
  }

  function initGallery() {
    const images = Array.from(document.querySelectorAll('.product__media-scroll-item img'));
    if (!images.length) return;
    const gallery = new GalleryOverlay();
    images.forEach((img) => img.addEventListener('click', () => gallery.open(images)));
  }

  /* ────────────────────────────────────────────────────────
     Indicateur de défilement
     Mobile : index de la photo d'après le défilement horizontal.
     Desktop : photo la plus proche du centre de l'écran.
  ──────────────────────────────────────────────────────── */
  function initScrollIndicator() {
    const items    = Array.from(document.querySelectorAll('.product__media-scroll-item'));
    const scroller = document.querySelector('.product__media-scroll');
    if (!items.length || !scroller) return;

    const mobileQuery = window.matchMedia('(max-width: 989px)');
    let currentIndex = -1;
    let ticking = false;

    function activeIndex() {
      if (mobileQuery.matches) {
        const width = scroller.clientWidth || 1;
        return Math.max(0, Math.min(items.length - 1, Math.round(scroller.scrollLeft / width)));
      }
      const center = window.innerHeight / 2;
      let closest = 0;
      let closestDistance = Infinity;
      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });
      return closest;
    }

    function update(force) {
      ticking = false;
      const index = activeIndex();
      if (index === currentIndex && force !== true) return;
      currentIndex = index;
      const selector = mobileQuery.matches
        ? '.scroll-indicator--mobile .scroll-indicator-dot'
        : '.scroll-indicator--desktop .scroll-indicator-dot';
      document.querySelectorAll(selector).forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    scroller.addEventListener('scroll', requestUpdate, { passive: true });
    mobileQuery.addEventListener('change', () => update(true));
    window.addEventListener('load', () => update(true));
    update(true);
  }

  /* ────────────────────────────────────────────────────────
     Bouton panier de la barre mobile
     Délègue au bouton d'envoi du formulaire produit Shopify.
  ──────────────────────────────────────────────────────── */
  function initMobileCTA() {
    const button = document.getElementById('mobileStickyAddToCart');
    if (!button) return;

    button.addEventListener('click', () => {
      const form = document.getElementById(`product-form-${button.dataset.sectionId}`)
                || document.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      const submit = form.querySelector('[name="add"], [type="submit"]');
      if (submit) submit.click();
      else if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  }

  /* ────────────────────────────────────────────────────────
     Initialisation
  ──────────────────────────────────────────────────────── */
  function init() {
    /* Accordéons : un seul ouvert à la fois. */
    new CollapsibleGroup(
      document.querySelectorAll('.product__accordion details'),
      ':scope > .accordion__content'
    );

    /* Sélecteur de taille desktop. */
    const desktopSize = new CollapsibleGroup(
      document.querySelectorAll('#desktop-size-drawer'),
      ':scope > .size-drawer__panel',
      { dismissible: true }
    );
    bindSizeButtons({
      buttons:   document.querySelectorAll('#desktop-size-drawer .size-item'),
      valueAttr: 'size',
      labelEl:   document.getElementById('selected-size-label-desktop'),
      drawer:    desktopSize.find('desktop-size-drawer'),
    });

    /* Barre mobile : couleur et taille, un seul ouvert à la fois. */
    const mobileBar = new CollapsibleGroup(
      document.querySelectorAll('.msb-drawer'),
      ':scope > .msb-drawer__panel',
      { dismissible: true }
    );
    bindSizeButtons({
      buttons:   document.querySelectorAll('.msb-size-swatch'),
      valueAttr: 'sizeValue',
      labelEl:   document.getElementById('msb-selected-size-value'),
      drawer:    mobileBar.find('msb-size-drawer'),
    });

    initGallery();
    initScrollIndicator();
    initMobileCTA();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();